// src/utils/promoData.js
// 滿額贈 / 折扣券：全站共用同一次請求。
// 網站一打開（main.js）就開始抓，與版本檢查同時進行；各頁面（購物車、右上角購物車、結帳、首頁、所有產品）
// 拿同一份結果，不再各自重抓、也不用排在版本檢查後面。
// force=true 時重新抓最新（例如滑鼠移到右上角購物車時在背景更新）。
import axios from 'axios'

let giftsPromise = null
let couponsPromise = null

export function loadGifts (force = false) {
  if (!giftsPromise || force) {
    giftsPromise = axios.get('/api/load/threshold-gifts')
      .then(res => (res.data && res.data.success ? (res.data.gifts || []) : []))
      .catch(() => { giftsPromise = null; return [] })
  }
  return giftsPromise
}

// 滿額贈：每一檔各自成立、可以疊加（例 滿 3000 送慕斯、滿 6000 送面膜 → 6000 慕斯＋面膜都送）
// 累贈（is_cumulative）：該檔每滿一次門檻送一份，例 滿 3000 送 1 → 6750 送 2；不累贈：該檔只送一份
// 購物車、右上角購物車、結帳頁共用；後端 validateEarnedGifts 同規則
export function computeEarnedGifts (gifts, subtotal) {
  const sub = Number(subtotal) || 0
  return (gifts || [])
    .filter(g => Number(g.min_amount) > 0 && sub >= Number(g.min_amount))
    .map(g => {
      const times = g.is_cumulative ? Math.floor(sub / Number(g.min_amount)) : 1
      return { ...g, gift_quantity: Number(g.gift_quantity) * times }
    })
}

// 下一個可以拿到的滿額贈：{ gift, shortfall(還差多少), more(已經有贈品、這次是再加贈) }
export function computeNextGift (gifts, subtotal) {
  const sub = Number(subtotal) || 0
  const hasAny = (gifts || []).some(g => Number(g.min_amount) > 0 && sub >= Number(g.min_amount))
  const options = []
  for (const g of gifts || []) {
    const min = Number(g.min_amount)
    if (!(min > 0)) continue
    if (sub < min) options.push({ gift: g, shortfall: min - sub, more: hasAny })
    else if (g.is_cumulative) options.push({ gift: g, shortfall: (Math.floor(sub / min) + 1) * min - sub, more: true })
  }
  if (!options.length) return null
  return options.reduce((a, b) => (a.shortfall <= b.shortfall ? a : b))
}

// 首頁/所有產品頁的「買越多送越多」階梯公告：折扣券與滿額贈依門檻由低到高排成一條
export function buildPromoSteps (gifts, coupons) {
  const steps = []
  for (const c of coupons || []) {
    steps.push({
      key: 'c' + c.id,
      min: Number(c.min_amount),
      text: `享 ${Number(c.discount_rate)} 折`,
      expire: c.days_left != null ? c.days_left : null,
    })
  }
  const sortedGifts = [...(gifts || [])].sort((a, b) => Number(a.min_amount) - Number(b.min_amount))
  sortedGifts.forEach((g, i) => {
    const qty = Number(g.gift_quantity) > 1 ? ` × ${g.gift_quantity}` : ''
    const verb = i === 0 ? '送' : '再加送'
    steps.push({
      key: 'g' + g.id,
      min: Number(g.min_amount),
      text: g.is_cumulative ? `每滿就${verb} ${g.gift_name}${qty}` : `${verb} ${g.gift_name}${qty}`,
      expire: null,
    })
  })
  return steps.sort((a, b) => a.min - b.min)
}

export function loadCoupons (force = false) {
  if (!couponsPromise || force) {
    couponsPromise = axios.get('/api/order/coupons')
      .then(res => (res.data && res.data.success ? (res.data.coupons || []) : []))
      .catch(() => { couponsPromise = null; return [] })
  }
  return couponsPromise
}
